import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Build,
  Category,
  Dashboard,
  Delete,
  Edit,
  Info,
  RequestQuote,
  ShoppingBag,
  TextFields,
  ViewCarousel,
} from '@mui/icons-material';
import useSnackbar from '../../hooks/useSnackbar';
import { ImageUpload } from '../../components/atoms/UploadImage';
import { IBanner } from '../../utils/entities/interface';
import { saveBanner, editBanner } from '../../services/banner';
import {
  APP_TYPES,
  BANNER_DISPLAYS,
  HomeConfig,
  HomeSection,
  SECTION_KEYS,
  SECTION_TYPES,
  createHomeConfig,
  deleteHomeConfig,
  getHomeConfigs,
  updateHomeConfig,
} from '../../services/homeConfig';

const PURPLE = '#7B1FA2';
const SURFACE = '#1C1C1C';
const SURFACE_ALT = '#232323';
const MUTED = '#8A8D91';

// folderType 7 = "homebanners" - same S3 folder the Banners gallery above uses.
const BANNER_FOLDER_TYPE = 7;

// Only these 3 section keys map to a positional HomeBanner slot (position
// 1/2/3). EXTRA_IMAGE and any other section have no 1:1 banner slot and are
// managed instead via the "Additional Banners" list on the parent page.
const POSITION_BY_BANNER_KEY: Record<string, number> = {
  TOP_BANNER: 1,
  MIDDLE_BANNER: 2,
  BOTTOM_BANNER: 3,
};
const SECTION_FOR_POSITION: Record<number, 'TOP' | 'MIDDLE' | 'BOTTOM'> = {
  1: 'TOP',
  2: 'MIDDLE',
  3: 'BOTTOM',
};

const humanize = (v: string) =>
  v
    ? v.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    : v;

const sectionIcon = (key: string) => {
  if (key.endsWith('BANNER')) return <ViewCarousel fontSize="small" />;
  if (key === 'SERVICE_TYPES') return <Dashboard fontSize="small" />;
  if (key === 'REQUEST') return <RequestQuote fontSize="small" />;
  if (key === 'UTILITIES') return <Build fontSize="small" />;
  if (key === 'PRODUCT_LIST') return <ShoppingBag fontSize="small" />;
  if (key === 'STERILIZATION_INFO') return <Info fontSize="small" />;
  if (key === 'INPUT') return <TextFields fontSize="small" />;
  return <Category fontSize="small" />;
};

const emptySection = (order: number): HomeSection => ({
  key: 'TOP_BANNER',
  type: 'BANNER',
  order,
  isActive: true,
  bannerDisplay: 'AUTO',
});

const emptyConfig = (): HomeConfig => ({
  appType: 'USER',
  screen: 'HOME',
  isActive: true,
  sections: [emptySection(1)],
});

const HomeConfigManager = ({
  banners = [],
  onBannersChanged,
}: {
  banners?: IBanner[];
  onBannersChanged?: () => void;
}) => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  // Map the 3 positional home banners to their layout slots so banner sections
  // can show the real image thumbnail inside a config card.
  const bannerByKey: Record<string, string | undefined> = {
    TOP_BANNER: banners.find((b) => b.position === 1)?.mediaUrl,
    MIDDLE_BANNER: banners.find((b) => b.position === 2)?.mediaUrl,
    BOTTOM_BANNER: banners.find((b) => b.position === 3)?.mediaUrl,
  };
  const bannerForKey = (key: string): IBanner | undefined => {
    const position = POSITION_BY_BANNER_KEY[key];
    return position ? banners.find((b) => b.position === position) : undefined;
  };
  const [configs, setConfigs] = useState<HomeConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<HomeConfig>(emptyConfig());
  // Pending banner-image edits made inline for BANNER sections, keyed by
  // section index within the open dialog. Cleared whenever the section list
  // is reordered/added/removed so a stale index can never apply to the wrong
  // section - falls back to bannerForKey(s.key) when absent for an index.
  const [sectionImages, setSectionImages] = useState<Record<number, string>>({});

  const [deleteTarget, setDeleteTarget] = useState<HomeConfig | null>(null);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      setConfigs(await getHomeConfigs());
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to load configs',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyConfig());
    setSectionImages({});
    setDialogOpen(true);
  };

  const openEdit = (config: HomeConfig) => {
    setEditingId(config._id || null);
    setForm({
      appType: config.appType,
      screen: config.screen,
      isActive: config.isActive,
      sections: (config.sections || []).map((s) => ({ ...s })),
      _id: config._id,
    });
    setSectionImages({});
    setDialogOpen(true);
  };

  const setSection = (index: number, patch: Partial<HomeSection>) => {
    setForm((prev) => {
      const sections = prev.sections.map((s, i) =>
        i === index ? { ...s, ...patch } : s,
      );
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setForm((prev) => ({
      ...prev,
      sections: [...prev.sections, emptySection(prev.sections.length + 1)],
    }));
    setSectionImages({});
  };

  const removeSection = (index: number) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, order: i + 1 })),
    }));
    setSectionImages({});
  };

  const moveSection = (index: number, dir: -1 | 1) => {
    setForm((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.sections.length) return prev;
      const sections = [...prev.sections];
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return { ...prev, sections: sections.map((s, i) => ({ ...s, order: i + 1 })) };
    });
    setSectionImages({});
  };

  const validate = (): string | null => {
    if (!form.appType) return 'App type is required';
    if (!form.screen.trim()) return 'Screen is required';
    if (!form.sections.length) return 'Add at least one section';
    for (let i = 0; i < form.sections.length; i += 1) {
      const s = form.sections[i];
      if (!s.key) return `Section ${i + 1}: key is required`;
      if (!s.type) return `Section ${i + 1}: type is required`;
      if (s.order == null || Number.isNaN(Number(s.order)))
        return `Section ${i + 1}: order is required`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      showSnackbar(err, 'error');
      return;
    }
    setSaving(true);
    try {
      // Persist any inline banner-image edits first, so the config's own save
      // never blocks on / mixes concerns with the banner document.
      let bannersChanged = false;
      for (let i = 0; i < form.sections.length; i += 1) {
        const s = form.sections[i];
        const position = POSITION_BY_BANNER_KEY[s.key];
        if (!position) continue;

        const newUrl = sectionImages[i];
        if (newUrl === undefined || !newUrl) continue; // untouched or cleared

        const existing = bannerForKey(s.key);
        if (existing?.mediaUrl === newUrl) continue; // unchanged

        const bannerPayload = {
          appType: existing?.appType || 'USER',
          mediaType: existing?.mediaType || 'IMAGE',
          mediaUrl: newUrl,
          thumbnailUrl: existing?.thumbnailUrl,
          section: SECTION_FOR_POSITION[position],
          order: existing?.order,
          destination: existing?.destination || 'ADVERTISE',
          position,
          data: existing?.data,
        };

        if (existing?._id) {
          await editBanner({ id: existing._id, ...bannerPayload });
        } else {
          await saveBanner(bannerPayload);
        }
        bannersChanged = true;
      }

      const payload: HomeConfig = {
        appType: form.appType,
        screen: form.screen.trim(),
        isActive: form.isActive,
        sections: form.sections.map((s) => ({
          key: s.key,
          type: s.type,
          order: Number(s.order),
          isActive: s.isActive,
          bannerDisplay: s.bannerDisplay || 'AUTO',
        })),
      };
      if (editingId) {
        await updateHomeConfig(editingId, payload);
        showSnackbar('Config updated', 'success');
      } else {
        await createHomeConfig(payload);
        showSnackbar('Config created', 'success');
      }
      setDialogOpen(false);
      loadConfigs();
      if (bannersChanged) onBannersChanged?.();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    try {
      await deleteHomeConfig(deleteTarget._id);
      showSnackbar('Config deleted', 'success');
      setDeleteTarget(null);
      loadConfigs();
    } catch (e) {
      showSnackbar(e instanceof Error ? e.message : 'Failed to delete', 'error');
    }
  };

  return (
    <Box>
      {SnackbarComponent}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: MUTED,
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          Screen Configurations
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{
            backgroundColor: PURPLE,
            '&:hover': { backgroundColor: '#5E167A' },
            textTransform: 'none',
            borderRadius: '10px',
          }}
        >
          Add Config
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      ) : configs.length === 0 ? (
        <Typography sx={{ color: MUTED, textAlign: 'center', py: 6 }}>
          No home configs yet. Create one to control the app home screen.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {configs.map((c) => {
            const sorted = (c.sections || [])
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            return (
              <Grid item xs={12} sm={6} md={4} key={c._id}>
                <Paper
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: SURFACE_ALT,
                    border: '1px solid #2F2F2F',
                    borderRadius: 3,
                    p: 2,
                    backgroundImage: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={c.appType}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(123,31,162,0.16)',
                        color: PURPLE,
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={c.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        bgcolor: c.isActive
                          ? 'rgba(11,230,0,0.14)'
                          : 'rgba(138,141,145,0.14)',
                        color: c.isActive ? '#0BE600' : MUTED,
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
                    {c.screen}
                  </Typography>
                  <Typography sx={{ color: MUTED, fontSize: 12, mb: 1.5 }}>
                    {sorted.length} section{sorted.length === 1 ? '' : 's'}
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    {sorted.map((s, i) => {
                      const img = s.key.endsWith('BANNER')
                        ? bannerByKey[s.key]
                        : undefined;
                      return (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.6,
                            opacity: s.isActive ? 1 : 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              flexShrink: 0,
                              bgcolor: '#2E2E2E',
                              border: '1px solid #3E3E3E',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: PURPLE,
                            }}
                          >
                            {img ? (
                              <img
                                src={img}
                                alt={s.key}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            ) : (
                              sectionIcon(s.key)
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                color: '#e2e8f0',
                                fontSize: 13,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {s.order}. {humanize(s.key)}
                            </Typography>
                            <Typography sx={{ color: MUTED, fontSize: 11 }}>
                              {humanize(s.type)}
                              {s.key.endsWith('BANNER') && s.bannerDisplay
                                ? ` · ${humanize(s.bannerDisplay)}`
                                : ''}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: s.isActive ? '#0BE600' : MUTED,
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  <Divider sx={{ borderColor: '#2F2F2F', my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(c)}
                      sx={{ color: PURPLE }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(c)}
                      sx={{ color: '#E31E25' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: SURFACE, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>
          {editingId ? 'Edit Home Config' : 'Create Home Config'}
        </DialogTitle>
        <Divider sx={{ borderColor: '#3E3E3E' }} />
        <DialogContent sx={{ backgroundColor: SURFACE }}>
          <Grid container spacing={2} sx={{ mt: 0.5, mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="App Type"
                value={form.appType}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    appType: e.target.value as HomeConfig['appType'],
                  }))
                }
              >
                {APP_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Screen"
                placeholder="HOME / STERILIZATION / …"
                value={form.screen}
                onChange={(e) =>
                  setForm((p) => ({ ...p, screen: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, isActive: e.target.checked }))
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: PURPLE },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: PURPLE,
                      },
                    }}
                  />
                }
                label={<Typography sx={{ color: '#fff' }}>Active</Typography>}
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
              mb: 1,
            }}
          >
            <Typography sx={{ color: MUTED }}>Sections (ordered)</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={addSection}
              sx={{ color: PURPLE, textTransform: 'none' }}
            >
              Add Section
            </Button>
          </Box>

          {form.sections.map((s, index) => (
            <Paper
              key={index}
              sx={{
                backgroundColor: SURFACE_ALT,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Key"
                    value={s.key}
                    onChange={(e) => setSection(index, { key: e.target.value })}
                  >
                    {SECTION_KEYS.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Type"
                    value={s.type}
                    onChange={(e) => setSection(index, { type: e.target.value })}
                  >
                    {SECTION_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Banner Display"
                    value={s.bannerDisplay || 'AUTO'}
                    onChange={(e) =>
                      setSection(index, { bannerDisplay: e.target.value })
                    }
                  >
                    {BANNER_DISPLAYS.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Order"
                    value={s.order}
                    onChange={(e) =>
                      setSection(index, { order: Number(e.target.value) })
                    }
                  />
                </Grid>
                <Grid item xs={4} sm={1}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={s.isActive}
                        onChange={(e) =>
                          setSection(index, { isActive: e.target.checked })
                        }
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: PURPLE },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            { backgroundColor: PURPLE },
                        }}
                      />
                    }
                    label={<Typography sx={{ color: MUTED, fontSize: 12 }}>On</Typography>}
                  />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveSection(index, -1)}
                        disabled={index === 0}
                        sx={{ color: MUTED }}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => moveSection(index, 1)}
                        disabled={index === form.sections.length - 1}
                        sx={{ color: MUTED }}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => removeSection(index)}
                    disabled={form.sections.length === 1}
                    sx={{ color: '#E31E25' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>

              {POSITION_BY_BANNER_KEY[s.key] && (
                <Box sx={{ mt: 1.5, maxWidth: 320 }}>
                  <Typography sx={{ color: MUTED, fontSize: 12, mb: 0.5 }}>
                    Banner Image
                  </Typography>
                  <ImageUpload
                    fileId={`section-banner-${index}`}
                    type="banner"
                    imageUrl={sectionImages[index] ?? bannerForKey(s.key)?.mediaUrl ?? ''}
                    onChange={(url) =>
                      setSectionImages((prev) => ({ ...prev, [index]: url }))
                    }
                    onRemove={() =>
                      setSectionImages((prev) => ({ ...prev, [index]: '' }))
                    }
                    uploadLabel="Upload Banner Image"
                    disabled={saving}
                    folderType={BANNER_FOLDER_TYPE}
                  />
                </Box>
              )}
            </Paper>
          ))}
        </DialogContent>
        <Divider sx={{ borderColor: '#3E3E3E' }} />
        <DialogActions sx={{ backgroundColor: SURFACE, p: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{ color: MUTED, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: PURPLE,
              '&:hover': { backgroundColor: '#5E167A' },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { backgroundColor: SURFACE, backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Delete Config</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: MUTED }}>
            Delete the {deleteTarget?.appType} · {deleteTarget?.screen} home
            config? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ color: MUTED, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeConfigManager;
